import { CoverStyle } from "@/types/agendaCustomization";
import { cn } from "@/lib/utils";

interface ProductPreviewProps {
  coverStyle: CoverStyle;
  designId?: string;
  engravingText?: string;
  engravingPosition?: string;
  engravingTypography?: string;
  engravingColor?: string;
  customDesignFile?: File;
}

export const ProductPreview = ({
  coverStyle,
  designId,
  engravingText,
  engravingPosition,
  engravingTypography,
  engravingColor,
  customDesignFile,
}: ProductPreviewProps) => {
  // Determinar posición del texto grabado
  const getTextPositionClasses = () => {
    switch (engravingPosition) {
      case "superior":
        return "top-8";
      case "inferior":
        return "bottom-8";
      case "esquina":
        return "top-8 right-8";
      case "centro":
      default:
        return "top-1/2 -translate-y-1/2";
    }
  };

  return (
    <div className="sticky top-4 space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">
          Vista Previa
        </h3>

        {/* Mockup de la agenda */}
        <div className="relative w-full aspect-[3/4] bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-lg overflow-hidden shadow-md">
          {/* Diseño de fondo */}
          {customDesignFile ? (
            <div className="absolute inset-0">
              <img
                src={URL.createObjectURL(customDesignFile)}
                alt="Diseño personalizado"
                className="w-full h-full object-cover"
              />
            </div>
          ) : designId ? (
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(135deg,
                  hsl(${(Math.random() * 360)}, 70%, 80%),
                  hsl(${(Math.random() * 360)}, 70%, 60%))`,
              }}
            />
          ) : null}

          {/* Overlay con textura de cuero simulado */}
          <div className="absolute inset-0 bg-black/5 backdrop-blur-[0.5px]" />

          {/* Texto grabado */}
          {engravingText && (
            <div
              className={cn(
                "absolute left-1/2 -translate-x-1/2 px-4 text-center transition-all duration-300",
                getTextPositionClasses()
              )}
            >
              <p
                className="text-lg md:text-xl font-medium tracking-wide"
                style={{
                  fontFamily: engravingTypography
                    ? `var(--font-${engravingTypography})`
                    : "Georgia, serif",
                  color: engravingColor || "#333",
                  textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                }}
              >
                {engravingText}
              </p>
            </div>
          )}

          {/* Indicador de estilo */}
          <div className="absolute bottom-4 left-4">
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-3 py-1 rounded-full">
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                {coverStyle.charAt(0).toUpperCase() + coverStyle.slice(1)}
              </span>
            </div>
          </div>
        </div>

        {/* Info adicional */}
        <div className="mt-4 space-y-2 text-xs text-gray-500 dark:text-gray-400">
          <p>• Vista previa aproximada</p>
          <p>• Los colores pueden variar ligeramente</p>
          {designId && <p>• Diseño: {designId}</p>}
        </div>
      </div>
    </div>
  );
};
